import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { z } from 'zod';

// src/error-codes.ts
var A11Y_LIB = "@norkron/web-a11y";
var A11Y_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.a11y");
var { warn, panic, reportInvalidInput } = createScopedErrors(A11Y_LIB, A11Y_TOKEN);
try {
  registerCodes(A11Y_LIB, {
    A11Y_INVALID_CONFIG: {
      description: "A hook received a configuration that violates its contract \u2014 e.g. a `value` pointing to a disabled tab, a non-array of tabs, or a missing aria id coordination target. The hook coerces to a safe fallback; consumers should fix the config.",
      severity: "warning",
      docsAnchor: "a11y/invalid-config",
      exampleFix: "Inspect the hook arguments at the call site and ensure the referenced ids/keys exist in the supplied collection."
    },
    WEB_A11Y_INVALID_INPUT: {
      description: "A public input to @norkron/web-a11y failed zod validation (Wave 10 schema mandate). Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
      severity: "warning",
      docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE"
    },
    A11Y_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck info finding: time budget exceeded while iterating live-region trackers; partial findings returned.",
      severity: "info",
      docsAnchor: "a11y/selfcheck-budget-exceeded",
      exampleFix: "Raise budgetMs in SelfCheckOptions, or reduce the number of tracked aria-live regions in a single audit."
    },
    A11Y_LIVE_REGION_SPAM: {
      description: "selfCheck finding: an aria-live region received updates faster than the screen-reader announcement threshold (~500ms). Rapid updates cause screen readers to skip or stutter.",
      severity: "info",
      docsAnchor: "a11y/live-region-spam",
      exampleFix: "Throttle live-region updates to >=500ms apart, or batch multiple state changes into a single announcement."
    },
    A11Y_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck info finding: the self-check threw internally and was caught; reported so callers can detect failures without halting the conformance run.",
      severity: "info",
      docsAnchor: "a11y/selfcheck-internal-error"
    }
  });
} catch {
}

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var looseOptions = z.object({}).passthrough();
var itemsLikeSchema = z.array(z.unknown());
var FindNextEnabledIndexInputSchema = z.object({
  items: itemsLikeSchema,
  from: z.number().int(),
  step: z.union([z.literal(1), z.literal(-1)]),
  loop: z.boolean().optional()
});
var FirstEnabledIndexInputSchema = z.object({
  items: itemsLikeSchema,
  isDisabled: z.unknown().optional()
});
var LastEnabledIndexInputSchema = FirstEnabledIndexInputSchema;
var IsElementRTLInputSchema = z.unknown();
var SafeFocusInputSchema = z.object({
  el: z.unknown(),
  options: z.object({}).passthrough().optional()
});
var baseHookOptionsSchema = looseOptions;
var UseRTLInputSchema = z.undefined().or(baseHookOptionsSchema);
var UseRovingTabIndexInputSchema = baseHookOptionsSchema;
var UseArrowKeyNavigationInputSchema = baseHookOptionsSchema;
var UseFocusTrapInputSchema = baseHookOptionsSchema;
var UseFocusRestoreInputSchema = baseHookOptionsSchema;
var UseDisclosureInputSchema = baseHookOptionsSchema;
var UseTypeaheadInputSchema = baseHookOptionsSchema;
var UseLiveAnnouncerInputSchema = z.undefined().or(
  z.object({
    politeness: z.enum(["polite", "assertive"]).optional(),
    atomic: z.boolean().optional(),
    clearAfterMs: z.number().nonnegative().optional()
  }).passthrough()
);
var LiveAnnouncerOptionsSchema = UseLiveAnnouncerInputSchema;
var CreateLiveAnnouncerInputSchema = z.object({
  el: z.unknown(),
  opts: LiveAnnouncerOptionsSchema.optional()
});
var UseLabelledByInputSchema = z.union([z.string(), z.null(), z.undefined()]);
var UseDescribedByInputSchema = z.union([z.string(), z.null(), z.undefined()]);
var UseComboboxInputSchema = baseHookOptionsSchema;
z.enum(["inline", "list"]);
var DefaultComboboxFilterInputSchema = z.object({
  items: z.array(z.unknown()),
  query: z.string(),
  toLabel: z.unknown()
});
var UseTabsInputSchema = baseHookOptionsSchema;

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: A11Y_LIB,
      libToken: A11Y_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/safeFocus.ts
function safeFocus(el) {
  validateInput(SafeFocusInputSchema, "safeFocus", { el });
  if (el == null) return false;
  if (typeof el.focus !== "function") return false;
  if (el.isConnected === false) return false;
  el.focus();
  return true;
}

// src/findNextEnabledIndex.ts
function findNextEnabledIndex(items, from, step, loop = false) {
  validateInput(FindNextEnabledIndexInputSchema, "findNextEnabledIndex", { items, from, step, loop });
  const n = items.length;
  if (n === 0) return -1;
  let i = from + step;
  let safety = n + 1;
  while (safety-- > 0) {
    if (i < 0) {
      if (!loop) return -1;
      i = n - 1;
    } else if (i >= n) {
      if (!loop) return -1;
      i = 0;
    }
    if (i === from) return -1;
    if (!items[i]?.disabled) return i;
    i += step;
  }
  return -1;
}
function firstEnabledIndex(items) {
  validateInput(FirstEnabledIndexInputSchema, "firstEnabledIndex", { items });
  for (let i = 0; i < items.length; i++) {
    if (!items[i]?.disabled) return i;
  }
  return -1;
}
function lastEnabledIndex(items) {
  validateInput(LastEnabledIndexInputSchema, "lastEnabledIndex", { items });
  for (let i = items.length - 1; i >= 0; i--) {
    if (!items[i]?.disabled) return i;
  }
  return -1;
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-a11y";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
var LIVE_REGION_TRACK = /* @__PURE__ */ new Map();
var RAPID_THRESHOLD_MS = 500;
function __recordLiveRegionUpdate(el) {
  const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const prev = LIVE_REGION_TRACK.get(el);
  if (!prev) {
    LIVE_REGION_TRACK.set(el, {
      lastUpdateAt: now,
      rapidUpdates: 0,
      totalUpdates: 1
    });
    return;
  }
  const dt = now - prev.lastUpdateAt;
  prev.totalUpdates += 1;
  if (dt < RAPID_THRESHOLD_MS) prev.rapidUpdates += 1;
  prev.lastUpdateAt = now;
}
function __resetLiveRegionTracker() {
  LIVE_REGION_TRACK.clear();
}
function selectorFor(el) {
  const id = el.getAttribute("id");
  if (id) return `#${id}`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    for (const [el, track] of LIVE_REGION_TRACK) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "A11Y_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (track.rapidUpdates > 0) {
        findings.push({
          libId: LIB_ID,
          code: "A11Y_LIVE_REGION_SPAM",
          category: "a11y",
          severity: "minor",
          message: `aria-live region received ${track.rapidUpdates} update(s) within <${RAPID_THRESHOLD_MS}ms of the previous announcement \u2014 screen readers will skip or stutter.`,
          selector: selectorFor(el),
          suggestedFix: "Throttle live-region updates to >=500ms apart, or batch multiple state changes into a single announcement.",
          extras: {
            rapidUpdates: track.rapidUpdates,
            totalUpdates: track.totalUpdates,
            thresholdMs: RAPID_THRESHOLD_MS
          }
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "A11Y_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/createLiveAnnouncer.ts
function roleFor(politeness) {
  return politeness === "assertive" ? "alert" : "status";
}
function applyLiveRegionAttrs(el, opts = {}) {
  const politeness = opts.politeness ?? "polite";
  el.setAttribute("aria-live", politeness);
  el.setAttribute("aria-atomic", opts.atomic ?? true ? "true" : "false");
  el.setAttribute("role", roleFor(politeness));
}
function createLiveAnnouncer(el, opts = {}) {
  validateInput(CreateLiveAnnouncerInputSchema, "createLiveAnnouncer", { el, opts });
  const clearAfterMs = opts.clearAfterMs ?? 0;
  applyLiveRegionAttrs(el, opts);
  let timer = null;
  function cancel() {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  }
  return {
    element: el,
    announce(message) {
      if (typeof message !== "string") return;
      if (message.length === 0) {
        el.textContent = "";
        cancel();
        return;
      }
      el.textContent = message;
      __recordLiveRegionUpdate(el);
      cancel();
      if (clearAfterMs > 0) {
        timer = setTimeout(() => {
          el.textContent = "";
          timer = null;
        }, clearAfterMs);
      }
    },
    destroy: cancel
  };
}

// src/version.ts
var LIB_VERSION = "0.9.5";

export { DefaultComboboxFilterInputSchema, IsElementRTLInputSchema, LIB_VERSION, UseArrowKeyNavigationInputSchema, UseComboboxInputSchema, UseDescribedByInputSchema, UseDisclosureInputSchema, UseFocusRestoreInputSchema, UseFocusTrapInputSchema, UseLabelledByInputSchema, UseLiveAnnouncerInputSchema, UseRTLInputSchema, UseRovingTabIndexInputSchema, UseTabsInputSchema, UseTypeaheadInputSchema, __recordLiveRegionUpdate, __resetLiveRegionTracker, applyLiveRegionAttrs, createLiveAnnouncer, findNextEnabledIndex, firstEnabledIndex, lastEnabledIndex, safeFocus, selfCheck, validateInput, warn };
//# sourceMappingURL=chunk-A4N37R74.js.map
//# sourceMappingURL=chunk-A4N37R74.js.map